import React from "react";
import { Pressable, StyleSheet, View } from "react-native";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ClockCounterClockwiseIcon,
  ChatCircleIcon,
} from "@phosphor-icons/react";

import CustomButton from "../../../../../components/common/general/CustomButton/CustomButton";
import CustomProgressBar from "../../../../../components/common/general/CustomProgressBar/CustomProgressBar";
import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import type { CourseModernizerReviewPermissions } from "../reviewPermissions";
import type { CourseEvaluatorReviewData } from "../types/reviewTypes";
import { colors } from "../../../../../styles/design-system";

interface ReviewTopBarProps {
  review: CourseEvaluatorReviewData;
  permissions: CourseModernizerReviewPermissions;
  reviewedProgress: number;
  lastSavedAt?: string | null;
  isSaving?: boolean;
  commentsCount?: number;
  unresolvedCommentsCount?: number;
  onExitReview: () => void;
  onOpenPublishSheet?: () => void;
  onOpenVersionHistory: () => void;
  onOpenCommentsSheet?: () => void;
  isHistoricalSnapshotView?: boolean;
}

function ReviewTopBar({
  review,
  permissions,
  reviewedProgress,
  lastSavedAt,
  isSaving,
  onExitReview,
  commentsCount = 0,
  unresolvedCommentsCount = 0,
  onOpenPublishSheet,
  onOpenVersionHistory,
  onOpenCommentsSheet,
  isHistoricalSnapshotView = false,
}: ReviewTopBarProps) {
  const hasUnresolvedComments = unresolvedCommentsCount > 0;
  const hasQueued = review.queuedCount > 0;
  const canOpenReadySheet = hasQueued && permissions.canPublish && !isHistoricalSnapshotView;
  const progressColor = reviewedProgress === 100 ? colors.success[500] : colors.brand[500];
  const readyActionText = review.canPublishToLms
    ? `Ready to Publish (${review.queuedCount})`
    : `Ready to Download (${review.queuedCount})`;

  return (
    <View style={styles.topBar}>
      <View style={styles.topBarLeft}>
        <CustomButton
          text="Exit Review"
          styleType="transparent"
          size="s"
          useTranslationText={false}
          onPress={onExitReview}
          style={styles.exitButton}
          textStyle={styles.exitButtonText}
          leftIcon={<ArrowLeftIcon size={18} color={colors.grey[700]} />}
        />

        <View style={styles.topBarDivider} />

        <View style={styles.titleRow}>
          <CustomText
            text={`${review.courseName} (${review.sourceLabel})`}
            useTranslationText={false}
            size="m"
            weight="bold"
            role="heading"
            aria-level="1"
            style={styles.topBarTitle}
          />
          <CustomText text={review.courseCode} useTranslationText={false} size="s" style={styles.topBarMeta} />
          <CustomText
            text={review.termLabel}
            useTranslationText={false}
            size="xs"
            weight="normal"
            style={styles.topBarMeta}
          />
          {permissions.showViewOnlyBadge && (
            <View style={styles.viewOnlyBadge}>
              <CustomText
                text="View only"
                useTranslationText={false}
                size="xs"
                weight="bold"
                style={styles.viewOnlyBadgeText}
              />
            </View>
          )}
        </View>
      </View>

      <View style={styles.topBarRight}>
        {/* Reviewed count */}
        <CustomText
          text={`${review.reviewedCount}/${review.totalCount}`}
          useTranslationText={false}
          size="xs"
          weight="medium"
          style={styles.reviewedCount}
        />
        <CustomText text="reviewed" useTranslationText={false} size="xs" weight="normal" style={styles.reviewedLabel} />

        {/* Progress bar */}
        <View style={styles.topProgressWrap}>
          <CustomProgressBar
            ariaLabel="Review progress"
            progress={reviewedProgress}
            color={progressColor}
            unfilledColor={colors.grey[100]}
          />
        </View>

        {/* Saved indicator — passive text, not a button */}
        {(lastSavedAt || isSaving) && (
          <View style={styles.savedRow}>
            <CheckCircleIcon size={12} color={colors.success[500]} weight="fill" />
            <CustomText
              text={isSaving ? "Saving..." : `Saved ${lastSavedAt}`}
              useTranslationText={false}
              size="xs"
              style={styles.savedText}
            />
          </View>
        )}

        <Pressable
          onPress={onOpenVersionHistory}
          style={styles.historyButton}
          accessibilityLabel="Open version history">
          <ClockCounterClockwiseIcon size={18} color={colors.grey[700]} weight="regular" />
        </Pressable>

        {onOpenCommentsSheet && (
          <Pressable
            onPress={onOpenCommentsSheet}
            style={styles.commentsButton}
            accessibilityLabel={commentsCount > 0 ? `Open comments (${commentsCount})` : "Open comments"}>
            <ChatCircleIcon size={18} color={colors.grey[700]} weight="regular" />
            {hasUnresolvedComments && <View style={styles.commentsIndicator} />}
          </Pressable>
        )}

        {/* Ready action — publish-capable LMS courses publish, uploaded courses download exports. */}
        {permissions.showPublishControl &&
          (canOpenReadySheet ? (
            <CustomButton
              text={readyActionText}
              useTranslationText={false}
              onPress={onOpenPublishSheet ?? (() => {})}
              styleType="primary"
              size="s"
              rightIcon={<ArrowRightIcon size={16} color={colors.white} />}
              style={styles.publishButtonActive}
              textStyle={styles.publishButtonActiveText}
            />
          ) : (
            <CustomButton
              text={readyActionText}
              useTranslationText={false}
              onPress={() => {}}
              disabled
              styleType="lightStroke"
              size="s"
              rightIcon={<ArrowRightIcon size={16} color={colors.grey[400]} />}
              style={styles.publishButton}
              textStyle={styles.publishButtonText}
            />
          ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  topBar: {
    height: 56,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    backgroundColor: "#FFFFFF",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
  },
  topBarLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
  },
  exitButton: {
    paddingHorizontal: 8,
    paddingVertical: 8,
  },
  exitButtonText: {
    color: colors.grey[700],
    fontSize: 12,
    fontWeight: "500",
  },
  topBarDivider: {
    width: 1,
    height: 24,
    backgroundColor: colors.grey[200],
    marginHorizontal: 16,
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
    flex: 1,
  },
  topBarTitle: {
    color: colors.grey[900],
  },
  topBarMeta: {
    color: colors.grey[500],
  },
  viewOnlyBadge: {
    height: 24,
    justifyContent: "center",
    paddingHorizontal: 10,
    borderRadius: 999,
    backgroundColor: colors.grey[100],
    borderWidth: 1,
    borderColor: colors.grey[200],
  },
  viewOnlyBadgeText: {
    color: colors.grey[700],
    fontSize: 11,
  },
  topBarRight: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginLeft: 24,
  },
  reviewedCount: {
    color: colors.grey[900],
  },
  reviewedLabel: {
    color: colors.grey[500],
  },
  topProgressWrap: {
    width: 80,
  },
  savedRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  savedText: {
    color: colors.grey[500],
    fontSize: 11,
  },
  commentsButton: {
    height: 28,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    position: "relative",
    backgroundColor: colors.white,
  },
  commentsIndicator: {
    position: "absolute",
    top: 5,
    right: 8,
    width: 6,
    height: 6,
    borderRadius: 999,
    backgroundColor: "#a855f7",
  },
  historyButton: {
    width: 34,
    height: 34,
    alignItems: "center",
    justifyContent: "center",
    padding: 8,
    borderRadius: 6,
    backgroundColor: "transparent",
  },
  publishButton: {
    borderRadius: 6,
    paddingHorizontal: 10,
    height: 28,
    borderColor: colors.grey[200],
    shadowColor: "transparent",
    elevation: 0,
  },
  publishButtonText: {
    color: colors.grey[400],
    fontSize: 12,
    fontWeight: "500",
  },
  publishButtonActive: {
    borderRadius: 6,
    paddingHorizontal: 10,
    height: 28,
  },
  publishButtonActiveText: {
    color: colors.white,
    fontSize: 12,
    fontWeight: "500",
  },
});

export default ReviewTopBar;
